import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getHomePageConfig } from 'src/modules/home/HomePageMapping.ts';
import { useFilterLogic } from 'src/modules/common/hooks/useFilterLogic.ts';
import { ConfigContext } from 'src/configContext.ts';
import {
    UserInputKindQuery,
} from 'src/generated/operations.ts';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { CalcVolumetricFlow } from 'src/modules/user-input/components/CalcVolumetricFlow.tsx';
import { getAllowedUnits } from 'src/modules/user-input/calcV100/unitsMapping.ts';
import { VolumetricFlowCalculatorInput } from 'src/modules/user-input/calcVolumetricFlow/types.ts';
import { useUserInputState } from 'src/modules/user-input/useUserInputState.ts';
import { useEmptyResult } from 'src/modules/user-input/useEmptyResult.ts';
import { Button } from 'src/common/Button/Button.tsx';
import { LoadingIcon } from 'src/modules/common/icons/LoadingIcon.tsx';
import { NavigationContainer } from 'src/modules/common/components/Navigation/NavigationContainer.tsx';
import { NavigationButton } from 'src/modules/common/components/Navigation/NavigationButton.tsx';
import { MainLayout } from 'src/modules/common/layouts/MainLayout.tsx';
import { useCheckMainFilter } from 'src/modules/common/hooks/useCheckMainFilter.ts';
import { useNavigate } from 'react-router-dom';
import { RuntimeContext } from 'src/runtimeContext';
import { InlineNotification } from 'src/modules/common/components/InlineNotification/InlineNotification.tsx';
import { getValvesPageConfig } from 'src/modules/valves/ValvesPageMapping.ts';
import { MainLayoutHeader } from 'src/modules/common/components/MainLayoutHeader/MainLayoutHeader.tsx';
import { ValveBreadcrumbs } from 'src/modules/common/components/ValveBreadcrumbs/ValveBreadcrumbs';
import { PATHS } from 'src/types/Paths';
import { FormFieldNames } from 'src/modules/user-input/FormFieldNames';
import { initTransferredUnits } from 'src/modules/user-input/InitInputState';
import { useGetHomeData } from 'src/modules/home/useGetHomeData';
import { buildReadableFilterStatus } from 'src/modules/home/buildReadableFilterStatus';
import { SelectionsButton } from 'src/projects/components/SelectionsButton.tsx';

export const UserInputPage = ({ data }: { data: UserInputKindQuery }): React.ReactNode => {
    const { userSettings, mandatoryFilterConfig } = useContext(ConfigContext);
    const { isMobile, isRevitPlugin } = useContext(RuntimeContext);

    const emptyResult = useEmptyResult();

    const [error, setError] = useState('');

    const [transferredUnits, setTransferredUnits] = useState(initTransferredUnits);

    const pageConfig = useMemo(() => getHomePageConfig(userSettings.currentRegion), [userSettings]);
    const valvesPageConfig = useMemo(() => getValvesPageConfig(userSettings.currentRegion), [userSettings]);
    if (pageConfig === null || valvesPageConfig === null) {
        throw new Error('No Page Config!');
    }

    const {
        state: inputState,
        searchParams,
        setState: setInputState,
        initialState,
        locationState,
        setLocationState,
    } = useUserInputState(valvesPageConfig.filterConfig);

    useEffect(() => {
        if (locationState.is6WayRequired !== data.UserInputKind.is6WayRequired) {
            setLocationState({
                ...locationState,
                is6WayRequired: data.UserInputKind.is6WayRequired,
            });
        }
    }, [data, locationState, setLocationState]);

    const [filter] = useFilterLogic(pageConfig.filterConfig, searchParams);

    const navigate = useNavigate();

    const homePageState = useGetHomeData({
        query: pageConfig.query,
        filterConfig: pageConfig.filterConfig,
        filter,
        mandatoryFilterConfig,
        currentLocale: userSettings.currentLocale,
        currentRegion: userSettings.currentRegion,
    });

    useCheckMainFilter({
        filter,
        navigate,
        searchParams,
        filterConfig: [],
    });

    const sequence1Handler = useCallback((userInput: VolumetricFlowCalculatorInput) => {
        const newInput = {
            ...inputState,
            kvs: userInput.kvs,
            kvs_u: userInput.kvs_u,
            dpmv: userInput.dpmv,
            dpmv_u: userInput.dpmv_u,
            maxDp: userInput.maxDp,
            maxDp_u: userInput.maxDp_u,
            v100: userInput.v100,
            v100_u: userInput.v100_u,
            inlet: userInput.inlet,
            inlet_u: userInput.inlet_u,
            outlet: userInput.outlet,
            outlet_u: userInput.outlet_u,
            mass: userInput.mass,
            mass_u: userInput.mass_u,
        };
        setInputState(newInput);
    }, [inputState, setInputState]);

    const sequence2Handler = useCallback((userInput: VolumetricFlowCalculatorInput) => {
        const newInput = {
            ...inputState,
            kvs_2: userInput.kvs,
            kvs_2_u: userInput.kvs_u,
            maxDp_2: userInput.maxDp,
            maxDp_2_u: userInput.maxDp_u,
            v100_2: userInput.v100,
            v100_2_u: userInput.v100_u,
            dpmv2: userInput.dpmv,
            dpmv2_u: userInput.dpmv_u,
        };
        setInputState(newInput);
    }, [inputState, setInputState]);

    const handleReset = useCallback(() => {
        setInputState({ ...initialState });
        setTransferredUnits(initTransferredUnits);
    }, [setInputState, initialState]);

    const allowedUnits = getAllowedUnits(userSettings.currentRegion);

    const navigation = () => (
        <NavigationContainer isMobile={isMobile}>
            <Button
                kind="ghost-secondary"
                size={isMobile ? 'md' : 'sm'}
                onClick={handleReset}
                fullWidth={isMobile}
                icon={(
                    <div className="vss-user-input-page__icon">
                        <LoadingIcon/>
                    </div>
                )}
            >
                <I18n messageId={MESSAGES.Reset}/>
            </Button>

            <NavigationButton
                type="forward"
                disabled={emptyResult}
                to={{
                    pathname: PATHS.Valves,
                    search: searchParams.toString(),
                }}
                eventData={JSON.stringify(inputState) === JSON.stringify(initialState) ? undefined : {
                    event: 'gaEvent',
                    eventCategory: 'sizing tool',
                    eventAction: 'step 2 - valve parameters',
                    eventLabel: undefined,
                }}
                isMobile={isMobile}
            >
                <I18n messageId={MESSAGES.next}/>
            </NavigationButton>
        </NavigationContainer>
    );

    const { isNotOCCO, is3WaySelected, is6WayRequired, kind } = data.UserInputKind;

    const fluid = filter?.Fluid || [];

    const notEmpty = FormFieldNames
        .filter((name) => {
            if (!is6WayRequired && name.includes('2')) {
                return false;
            }
            return inputState[name];
        }).length !== 0;

    return (
        <>
            <MainLayout>
                <MainLayoutHeader
                    titleId={MESSAGES.userInputPageTitle}
                    navigation={isMobile ? null : navigation()}
                    breadcrumbs={(
                        <ValveBreadcrumbs
                            currentPage={PATHS.UserInput}
                            homeValue={homePageState.data
                                ? buildReadableFilterStatus(homePageState.filter, homePageState.data)
                                : ''}
                            userInputState={inputState}
                            isUserInputPageEmptyResult={emptyResult}
                            locationState={locationState}
                            searchParams={searchParams}
                            resetHandler={notEmpty ? handleReset : undefined}
                        />
                    )}
                    selectionButton={!isRevitPlugin && (
                        <SelectionsButton
                            isMobile={isMobile}
                            locationState={locationState}
                            searchParams={searchParams}
                        />
                    )}
                />
                <>
                    {emptyResult && (
                        <InlineNotification
                            type="error"
                            message={<I18n messageId={MESSAGES.WE_RE_SORRY}/>}
                        />
                    )}

                    {error && (
                        <InlineNotification type="error" message={<I18n messageId={error}/>}/>
                    )}
                    <div className="vss-user-input-page">
                        <CalcVolumetricFlow
                            separatorTitle={is6WayRequired
                                ? <I18n messageId={MESSAGES.userInputSequence1}/>
                                : null}
                            setError={setError}
                            sequenceKey={1}
                            allowedUnits={allowedUnits}
                            currentLocale={userSettings.currentLocale}
                            currentRegion={userSettings.currentRegion}
                            isModulating={isNotOCCO}
                            is3WaySelected={is3WaySelected}
                            kind={kind}
                            fluid={fluid}
                            initialState={initialState}
                            input={{
                                v100: inputState.v100,
                                v100_u: inputState.v100_u,
                                dpmv: inputState.dpmv,
                                dpmv_u: inputState.dpmv_u,
                                maxDp: inputState.maxDp,
                                maxDp_u: inputState.maxDp_u,
                                mass: inputState.mass,
                                mass_u: inputState.mass_u,
                                inlet: inputState.inlet,
                                inlet_u: inputState.inlet_u,
                                outlet: inputState.outlet,
                                outlet_u: inputState.outlet_u,
                                kvs: inputState.kvs,
                                kvs_u: inputState.kvs_u,
                            }}
                            transferredUnits={transferredUnits}
                            setTransferredUnits={setTransferredUnits}
                            onChanged={sequence1Handler}
                        />

                        {is6WayRequired && (
                            <CalcVolumetricFlow
                                is3WaySelected={is3WaySelected}
                                separatorTitle={<I18n messageId={MESSAGES.userInputSequence2}/>}
                                setError={setError}
                                sequenceKey={2}
                                allowedUnits={allowedUnits}
                                currentLocale={userSettings.currentLocale}
                                currentRegion={userSettings.currentRegion}
                                isModulating={isNotOCCO}
                                kind={kind}
                                fluid={fluid}
                                initialState={initialState}
                                input={{
                                    ...initialState,
                                    v100: inputState.v100_2,
                                    v100_u: inputState.v100_2_u,
                                    dpmv: inputState.dpmv2,
                                    dpmv_u: inputState.dpmv2_u,
                                    maxDp: inputState.maxDp_2,
                                    maxDp_u: inputState.maxDp_2_u,
                                    kvs: inputState.kvs_2,
                                    kvs_u: inputState.kvs_2_u,
                                    mass: inputState.mass,
                                    mass_u: inputState.mass_u,
                                    inlet: inputState.inlet,
                                    inlet_u: inputState.inlet_u,
                                    outlet: inputState.outlet,
                                    outlet_u: inputState.outlet_u,
                                }}
                                transferredUnits={transferredUnits}
                                setTransferredUnits={setTransferredUnits}
                                onChanged={sequence2Handler}
                            />
                        )}
                    </div>
                </>
            </MainLayout>

            {isMobile && navigation()}
        </>
    );
};
